import React, { useState } from 'react';

import TpDialog from '../shared/tp_dialog';
import { discard_guard_text } from '../shared/dialog_logic';
import { DialogFooter, ErrorBox, FieldError, NoteField, RowContext } from './dialog_parts';
import { rupees } from './adapters';
import { KIND } from './error_handlers';
import { MODE, correct_form_errors } from './form_errors';
import {
  SESSION_TYPES,
  amount_from_pct,
  parse_number,
  pct_from_amount,
  reason_options,
  unchanged_since
} from './params';

/*
 * PROD-7828 R6 — "Correct penalty" (D81): reclassify and/or change the percentage or the amount. A
 * correction is never a waiver (MIXED_EDIT); waiving is its own dialog and its own audit row. Props-only:
 * the session page's Change Penalty modal mounts this same form. on_submit gets the form with only the
 * edited figure filled; the server derives the other from base_rate_used.
 */
// The figures and type the form starts from: the row's current values.
const row_figures = (row) => ({
  penalty_type: row.penalty_type,
  penalty_pct: String(row.penalty_pct),
  amount: String(row.basis)
});

const CorrectForm = ({ row, saving, error, on_submit, on_close, on_focus_lost }) => {
  const by_pct = !!row.session_uuid && row.base_rate > 0;
  const [mode, setMode] = useState(by_pct ? MODE.PCT : MODE.AMOUNT);
  const [form, setForm] = useState({ ...row_figures(row), reason_code: '', note: '' });
  // A VERSION_CONFLICT hands over the current row: its figures replace the stale ones (reason and note stay).
  const [seeded_row, setSeededRow] = useState(row);
  if (seeded_row !== row) {
    setSeededRow(row);
    setForm((f) => ({ ...f, ...row_figures(row) }));
    if (!by_pct) setMode(MODE.AMOUNT);
  }
  const [touched, setTouched] = useState(false);
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const errors = correct_form_errors(form, mode);
  const server = (error && error.fields) || {};

  const preview =
    mode === MODE.PCT
      ? amount_from_pct(row.base_rate, parse_number(form.penalty_pct))
      : pct_from_amount(row.base_rate, parse_number(form.amount));

  const body = {
    ...form,
    penalty_pct: mode === MODE.PCT ? form.penalty_pct : '',
    amount: mode === MODE.AMOUNT ? form.amount : ''
  };
  const [sent, setSent] = useState(null);
  const submit = () => {
    setTouched(true);
    if (Object.values(errors).some(Boolean)) return;
    setSent(body);
    on_submit(body);
  };
  // CONFIRM_LARGE_REQUIRED (CD-190): the confirm resends the refused body with its flag. After an edit
  // the footer offers a plain save again, so a confirm never covers an amount it was not asked about.
  const confirmable = !!error && error.kind === KIND.CONFIRM && unchanged_since(sent, body);
  const shown = error && error.kind === KIND.CONFIRM && !confirmable ? null : error;
  const confirm = () => on_submit({ ...sent, [error.confirm]: true });

  return (
    <TpDialog
      title_id="tp-pe-correct-title"
      busy={saving}
      guard_text={discard_guard_text(form.note)}
      on_close={on_close}
      on_focus_lost={on_focus_lost}
    >
      <div className="tp-modal">
        <h4 id="tp-pe-correct-title">Correct penalty</h4>
        <RowContext row={row} />
        <div className="tp-field-2">
          <div className="tp-field">
            <label htmlFor="tp-pe-correct-type">Penalty type</label>
            {row.session_uuid ? (
              <select
                id="tp-pe-correct-type"
                className="tp-select"
                value={form.penalty_type}
                onChange={(e) => set('penalty_type', e.target.value)}
              >
                {SESSION_TYPES.map((t) => (
                  <option key={t.penalty_type} value={t.penalty_type}>
                    {t.label}
                  </option>
                ))}
              </select>
            ) : (
              <input id="tp-pe-correct-type" className="tp-input" value={row.type_text} readOnly />
            )}
            <FieldError text={server.penalty_type} />
          </div>
          {by_pct ? (
            <div className="tp-field">
              <span className="tp-card-field-label">Change by</span>
              <div className="tp-seg" role="group" aria-label="Change by">
                <button
                  type="button"
                  className={mode === MODE.PCT ? 'on' : ''}
                  aria-pressed={mode === MODE.PCT}
                  onClick={() => setMode(MODE.PCT)}
                >
                  Percentage
                </button>
                <button
                  type="button"
                  className={mode === MODE.AMOUNT ? 'on' : ''}
                  aria-pressed={mode === MODE.AMOUNT}
                  onClick={() => setMode(MODE.AMOUNT)}
                >
                  Amount
                </button>
              </div>
            </div>
          ) : null}
        </div>
        <div className="tp-field-2">
          {mode === MODE.PCT ? (
            <div className="tp-field">
              <label htmlFor="tp-pe-correct-value">Penalty %</label>
              <input
                id="tp-pe-correct-value"
                className="tp-input"
                inputMode="decimal"
                value={form.penalty_pct}
                onChange={(e) => set('penalty_pct', e.target.value)}
              />
              <FieldError text={(touched && errors.value) || server.penalty_pct} />
            </div>
          ) : (
            <div className="tp-field">
              <label htmlFor="tp-pe-correct-value">Penalty amount (₹)</label>
              <input
                id="tp-pe-correct-value"
                className="tp-input"
                inputMode="decimal"
                value={form.amount}
                onChange={(e) => set('amount', e.target.value)}
              />
              <FieldError text={(touched && errors.value) || server.amount} />
            </div>
          )}
          <div className="tp-field">
            <span className="tp-card-field-label">
              {mode === MODE.PCT ? 'Amount' : 'Percentage'}
            </span>
            <span className="tp-hint">
              {preview === null
                ? 'Worked out by the server'
                : mode === MODE.PCT
                  ? `${rupees(preview)} of the ${rupees(row.base_rate)} adjusted fee`
                  : `${preview}% of the ${rupees(row.base_rate)} adjusted fee`}
            </span>
          </div>
        </div>
        <div className="tp-field">
          <label htmlFor="tp-pe-correct-reason">Reason</label>
          <select
            id="tp-pe-correct-reason"
            className="tp-select"
            value={form.reason_code}
            onChange={(e) => set('reason_code', e.target.value)}
          >
            <option value="">Choose a reason</option>
            {reason_options('correct').map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <FieldError text={(touched && errors.reason_code) || server.reason_code} />
        </div>
        <NoteField
          id="tp-pe-correct-note"
          label="Note (internal, required)"
          value={form.note}
          on_change={(v) => set('note', v)}
          error_text={(touched && errors.note) || server.note}
          placeholder="Why the penalty is being corrected"
        />
        <div className="tp-hint">
          The month recalculates when you save. To waive the penalty instead, use Waive.
        </div>
        <ErrorBox error={shown} />
        <DialogFooter
          saving={saving}
          error={shown}
          submit_label="Save correction"
          on_submit={submit}
          on_confirm={confirm}
          on_close={on_close}
        />
      </div>
    </TpDialog>
  );
};

export default CorrectForm;
